import React, { useState } from "react";
import { Button, Checkbox, Form, Header, Icon, Label, List, Message, Segment, Table } from "semantic-ui-react";
import { formatSoldDate } from "./pricingStrategyFormatting";

const STRENGTH_COLORS = { Strong: "green", Moderate: "yellow", "Context Only": "grey" };

const LOCATION_LABELS = {
  same_complex: "Same complex",
  same_subdivision: "Same subdivision",
  same_neighborhood: "Same neighborhood",
  nearby: "Nearby",
  expanded: "Expanded area",
};

const COMPONENT_LABELS = {
  propertyType: "Property type",
  location: "Location",
  aboveGrade: "Above-grade size",
  basement: "Finished basement",
  physical: "Beds / baths / age",
  recency: "Sale recency",
  transaction: "Arm's-length",
};

// Minimum length for the reason an agent must give to include a
// questionable sale - long enough that it can't be a placeholder.
export const MIN_INCLUSION_REASON_LENGTH = 10;

function money(value) {
  return typeof value === "number" ? `$${value.toLocaleString()}` : value || "-";
}

function sqft(value) {
  return Number.isFinite(value) ? `${value.toLocaleString()} sq ft` : "Unknown";
}

function componentText(key, component) {
  if (!component) return "Unknown";
  const status = String(component.status || "unknown").replace(/_/g, " ");
  if (key === "aboveGrade" && Number.isFinite(component.differencePercent)) return `${status} (${component.differencePercent > 0 ? "+" : ""}${component.differencePercent}%)`;
  if (key === "basement" && Number.isFinite(component.differenceSqft)) return `${status} (${component.differenceSqft > 0 ? "+" : ""}${component.differenceSqft} sq ft)`;
  if (key === "recency" && Number.isFinite(component.monthsAgo)) return `${status} (${component.monthsAgo} mo)`;
  if (key === "location" && component.detail) return component.detail;
  return status;
}

function CompDetails({ comp }) {
  const quality = comp.compQuality || {};
  return (
    <div style={{ fontSize: "0.9em" }}>
      {Array.isArray(quality.reasons) && quality.reasons.length > 0 && (
        <>
          <strong>Why this quality</strong>
          <List bulleted size="small" items={quality.reasons} style={{ marginTop: "2px" }} />
        </>
      )}
      {quality.components && (
        <Table compact="very" basic="very" size="small" style={{ marginTop: "4px" }}>
          <Table.Body>
            {Object.keys(COMPONENT_LABELS).map((key) => (
              <Table.Row key={key}>
                <Table.Cell collapsing>{COMPONENT_LABELS[key]}</Table.Cell>
                <Table.Cell>{componentText(key, quality.components[key])}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      {Array.isArray(comp.limitations) && comp.limitations.length > 0 && (
        <>
          <strong>Limitations</strong>
          <List bulleted size="small" items={comp.limitations} style={{ marginTop: "2px" }} />
        </>
      )}
      <p className="text-grey" style={{ marginTop: "4px" }}>
        Reported floor size (definition varies by record): {sqft(comp.squareFeet)}
        {Number.isFinite(comp.belowGradeUnfinishedSqft) && comp.belowGradeUnfinishedSqft > 0 ? ` · Unfinished basement: ${sqft(comp.belowGradeUnfinishedSqft)}` : ""}
        {comp.bedrooms && comp.bathrooms ? ` · ${comp.bedrooms} bed / ${comp.bathrooms} bath` : ""}
        {comp.yearBuilt ? ` · Built ${comp.yearBuilt}` : ""}
      </p>
    </div>
  );
}

function CompRow({ comp, included, onToggle, expanded, onExpand, toggleLabel }) {
  const strength = comp.compQuality?.maxCompStrength;
  const hasWarnings = (comp.limitations || []).length > 0 || (comp.areaWarnings || []).length > 0;
  return (
    <>
      <Table.Row disabled={!included}>
        <Table.Cell collapsing>
          <Checkbox checked={included} onChange={onToggle} aria-label={toggleLabel} />
        </Table.Cell>
        <Table.Cell>
          <div>{comp.address || "Unknown address"}</div>
          <div className="text-grey" style={{ fontSize: "0.85em" }}>{comp.propertyType || "Type unknown"}</div>
        </Table.Cell>
        <Table.Cell>
          <div>{money(comp.soldPrice)}</div>
          <div className="text-grey" style={{ fontSize: "0.85em" }}>{formatSoldDate(comp.soldDate)}</div>
        </Table.Cell>
        <Table.Cell>{sqft(comp.aboveGradeSqft)}</Table.Cell>
        <Table.Cell>{sqft(comp.belowGradeFinishedSqft)}</Table.Cell>
        <Table.Cell>
          <div>{LOCATION_LABELS[comp.locationTier] || "Unknown"}{Number.isFinite(comp.distanceMiles) ? ` · ${comp.distanceMiles} mi` : ""}</div>
          {comp.subdivision && <div className="text-grey" style={{ fontSize: "0.85em" }}>{comp.subdivision}</div>}
        </Table.Cell>
        <Table.Cell>
          {strength ? <Label size="tiny" color={STRENGTH_COLORS[strength]}>{strength}</Label> : "-"}
          {hasWarnings && <Icon name="warning circle" color="orange" title="Has data limitations" style={{ marginLeft: "4px" }} />}
        </Table.Cell>
        <Table.Cell collapsing>
          <Button type="button" basic size="mini" icon={expanded ? "chevron up" : "chevron down"} onClick={onExpand} aria-label="Show details" />
        </Table.Cell>
      </Table.Row>
      {expanded && (
        <Table.Row>
          <Table.Cell />
          <Table.Cell colSpan={7}><CompDetails comp={comp} /></Table.Cell>
        </Table.Row>
      )}
    </>
  );
}

/**
 * Verified comparable sales used for a pricing result, with include/exclude
 * per property, and a separate Review Candidates section for questionable
 * transactions that require an explicit, reasoned inclusion.
 */
export default function ComparableReviewPanel({
  comps,
  excludedIndices,
  onToggleComp,
  reviewCandidates,
  reviewInclusions,
  onIncludeReviewCandidate,
  onRemoveReviewInclusion,
  compSetStrength,
  stale,
  onRegenerate,
  regenerating,
}) {
  const [expandedKey, setExpandedKey] = useState(null);
  const [draftFor, setDraftFor] = useState(null);
  const [draftReason, setDraftReason] = useState("");
  const [showReview, setShowReview] = useState(false);

  if ((!comps || comps.length === 0) && (!reviewCandidates || reviewCandidates.length === 0)) return null;
  const includedCount = (comps || []).filter((_, idx) => !excludedIndices.has(idx)).length + (reviewCandidates || []).filter((c) => reviewInclusions.has(c.address)).length;

  return (
    <Segment>
      <Header as="h5" style={{ marginBottom: "4px" }}>
        <Icon name="home" />
        Verified Comparable Sales ({includedCount} selected)
      </Header>
      <p className="text-grey" style={{ fontSize: "0.9em" }}>
        Closed sales from public records, ranked by property type, location, size and recency. Uncheck a sale to leave
        it out; the recommendation must then be regenerated.
      </p>

      {stale && (
        <Message warning size="small">
          <Message.Header>Comparable set changed</Message.Header>
          <p>
            The price recommendation was calculated with a different set of comparable sales. Regenerate before using
            or sharing it.
          </p>
          {onRegenerate && (
            <Button type="button" size="small" primary loading={regenerating} disabled={regenerating} onClick={onRegenerate}>
              Regenerate with these comparables
            </Button>
          )}
        </Message>
      )}

      {compSetStrength?.limitation && <Message info size="small" content={compSetStrength.limitation} />}

      {comps && comps.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <Table celled compact size="small" unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing>Use</Table.HeaderCell>
                <Table.HeaderCell>Property</Table.HeaderCell>
                <Table.HeaderCell>Sale</Table.HeaderCell>
                <Table.HeaderCell>Above grade</Table.HeaderCell>
                <Table.HeaderCell>Fin. basement</Table.HeaderCell>
                <Table.HeaderCell>Location</Table.HeaderCell>
                <Table.HeaderCell>Quality</Table.HeaderCell>
                <Table.HeaderCell collapsing />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {comps.map((comp, idx) => (
                <CompRow
                  key={`c-${comp.address}-${idx}`}
                  comp={comp}
                  included={!excludedIndices.has(idx)}
                  onToggle={() => onToggleComp(idx)}
                  expanded={expandedKey === `c-${idx}`}
                  onExpand={() => setExpandedKey(expandedKey === `c-${idx}` ? null : `c-${idx}`)}
                  toggleLabel={`Include ${comp.address}`}
                />
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {reviewCandidates && reviewCandidates.length > 0 && (
        <Segment secondary style={{ marginTop: "12px" }}>
          <Header as="h5" style={{ marginBottom: "4px", cursor: "pointer" }} onClick={() => setShowReview(!showReview)}>
            <Icon name={showReview ? "caret down" : "caret right"} />
            Review Candidates ({reviewCandidates.length})
          </Header>
          <p className="text-grey" style={{ fontSize: "0.9em" }}>
            Sales that may not reflect open-market value (for example, estate or family transfers). They are left out
            unless you include one and record why.
          </p>
          {showReview && reviewCandidates.map((comp) => {
            const reason = reviewInclusions.get(comp.address);
            const drafting = draftFor === comp.address;
            return (
              <Segment key={`r-${comp.address}`} size="small">
                <div>
                  <strong>{comp.address}</strong> · {comp.propertyType || "Type unknown"} · {money(comp.soldPrice)} on {formatSoldDate(comp.soldDate)}
                </div>
                <div className="text-grey" style={{ fontSize: "0.85em" }}>
                  Above grade {sqft(comp.aboveGradeSqft)} · Fin. basement {sqft(comp.belowGradeFinishedSqft)} · {LOCATION_LABELS[comp.locationTier] || "Unknown location"}
                </div>
                <Message warning size="tiny" content={comp.reviewReason} style={{ margin: "6px 0" }} />
                {reason ? (
                  <>
                    <p style={{ fontSize: "0.9em" }}><Icon name="check" color="green" />Included. Your reason: {reason}</p>
                    <Button type="button" size="mini" basic onClick={() => onRemoveReviewInclusion(comp.address)}>Remove</Button>
                  </>
                ) : drafting ? (
                  <Form size="small">
                    <Form.TextArea
                      label="Why is this sale a valid comparable?"
                      placeholder="e.g. Estate sale, but listed on MLS for 30 days with normal marketing"
                      value={draftReason}
                      onChange={(e, { value }) => setDraftReason(value)}
                      rows={2}
                    />
                    <Button
                      type="button"
                      size="mini"
                      primary
                      disabled={draftReason.trim().length < MIN_INCLUSION_REASON_LENGTH}
                      onClick={() => {
                        onIncludeReviewCandidate(comp.address, draftReason.trim());
                        setDraftFor(null);
                        setDraftReason("");
                      }}
                    >
                      Include with this reason
                    </Button>
                    <Button type="button" size="mini" basic onClick={() => { setDraftFor(null); setDraftReason(""); }}>Cancel</Button>
                  </Form>
                ) : (
                  <Button type="button" size="mini" basic onClick={() => { setDraftFor(comp.address); setDraftReason(""); }}>
                    Include…
                  </Button>
                )}
              </Segment>
            );
          })}
        </Segment>
      )}
    </Segment>
  );
}
